import { useState, useEffect } from 'react';
import { elasticSearchBrandListByBody } from '@src/utils/elasticSearch';
import { groupBrandListByFirstLetter } from '@src/utils';
import { getInvisibleBrandIds } from '@src/utils/constants';
import { useInvisibleProductIds } from "@src/hooks/useInvisibleProductIds";

const BRAND_LIST_CACHE_KEY = 'cachingBrandList';
const BRAND_LIST_CACHE_TIMESTAMP_KEY = 'brandListCacheTimestamp';
const BRAND_LIST_CACHE_DURATION = 24 * 60 * 60 * 1000; // 1 days
const BRAND_LIST_REQUEST_SIZE = 5000;

const defaultRequestBody = {
  size: BRAND_LIST_REQUEST_SIZE,
  sort: [{ 'name.keyword': 'asc' }],
};

const returnAlphabetRangesBody = (value) => {
  return {
    ...defaultRequestBody,
    query: {
      bool: {
        must: [
          {
            regexp: {
              'name.keyword': {
                value,
              },
            },
          },
          {
            term: {
              is_published: true,
            },
          },
        ],
      },
    },
  };
};

const useBrandList = () => {
  const [groupedBrandList, setGroupedBrandList] = useState(null);
  const [fetchBrandListLoading, setFetchBrandListLoading] = useState(true);
  const ready = useInvisibleProductIds();

  const fetchBrandListByAlphabetRange = () => {
    const request_list = [];

    const alphabetRanges = ['[^a-zA-Z]+.*', '[a-cA-C]+.*', '[d-gD-G]+.*', '[h-lH-L]+.*', '[m-rM-R]+.*', '[s-zS-Z]+.*'];

    alphabetRanges.forEach((range) => {
      const body = returnAlphabetRangesBody(range);

      request_list.push(elasticSearchBrandListByBody(body));
    });

    Promise.all(request_list)
        .then((res) => {
        const invisibleIds = getInvisibleBrandIds();
        const filtered = res.map((r) =>
            r.filter((item) => {
              const match = item?.url?.match(/-(\d+)\/?$/);
              const idFromUrl = match?.[1];
              return idFromUrl && !invisibleIds.includes(idFromUrl);
            })
        );

        const brandListHash = groupBrandListByFirstLetter(filtered);
        setGroupedBrandList(brandListHash);
        setFetchBrandListLoading(false);
        localStorage.setItem(BRAND_LIST_CACHE_KEY, JSON.stringify(brandListHash));
        localStorage.setItem(BRAND_LIST_CACHE_TIMESTAMP_KEY, new Date().getTime().toString());
      })
      .catch((error) => {
        console.error('Error fetching brand data:', error);
      });
  };

  const checkingBrandListCacheAndFetchBrandList = () => {
    const cachedData = localStorage.getItem(BRAND_LIST_CACHE_KEY);
    const cachedTimestamp = localStorage.getItem(BRAND_LIST_CACHE_TIMESTAMP_KEY);

    if (cachedData && cachedTimestamp) {
      const now = new Date().getTime();
      const diff = now - Number(cachedTimestamp);

      if (diff < BRAND_LIST_CACHE_DURATION) {
        setGroupedBrandList(JSON.parse(cachedData));
        setFetchBrandListLoading(false);
        return;
      }
    }

    fetchBrandListByAlphabetRange();
  };

  useEffect(() => {
    ready && checkingBrandListCacheAndFetchBrandList();
  }, [ready]);

  return { groupedBrandList, fetchBrandListLoading };
};

export default useBrandList;
